import moment from "moment"
import { DEFAULT_NAV_PAGE, DEFAULT_NAV_SIZE, getDateComparatorStr, getGroupFromQueryString, GROUP_LIST } from "./utils"

/*
 **********************************************
 * Query string builders
 * 
 * Handle different options regarding query filters
 **********************************************
 */

const LoanFilterParamNames = {
  client: 'client',
  affiliation: 'affiliation',
  bundle: 'bundle',
  collateral: 'collateral',
  imei: 'imei',
  createdBy: 'createdBy',
  assignTo: 'assignTo',
  status: 'status',
  date: 'date',
  dateCmp: 'dateCmp'
},


/**
  * Check conditions and go to route
  * Only update if the value is different from the current URL param
  */
checkAndRoute = (paramName, currentValue, shouldSet, shouldReset, context, location) => {

 const { query } = location,
 currentParam = query[paramName] || ''

 if (currentParam.toLowerCase() !== currentValue.toString().toLowerCase()) {

    const newQuery = { ...query }
    if (shouldSet) {
      newQuery[paramName] = currentValue
    }
    else {
      delete newQuery[paramName]
    }

    if (shouldReset) {
      delete newQuery['page']
    }

    context.router.push({
      pathname: location.pathname,
      query: newQuery,
    })
  }
},

// Extract filter params from the object returned by BootstrapTable
getLoanFiltersFromBootstrapTable = filterObj => {

  const filters = {}
  for (const key in filterObj) {

    const valueObj = filterObj[key].value
    switch (key) {

      case 'clientName':
        filters[LoanFilterParamNames.client] = valueObj
        break

      case 'affiliated':
        filters[LoanFilterParamNames.affiliation] = valueObj
        break

      case 'bundleName':
        filters[LoanFilterParamNames.bundle] = valueObj
        break

      case 'collateralName':
        filters[LoanFilterParamNames.collateral] = valueObj
        break

      case 'imeisList':
        filters[LoanFilterParamNames.imei] = valueObj
        break

      case 'createdByName':
        filters[LoanFilterParamNames.createdBy] = valueObj
        break

      case 'assignToName':
        filters[LoanFilterParamNames.assignTo] = valueObj
        break

      case 'status':
        filters[LoanFilterParamNames.status] = valueObj
        break

      case 'disburseDateFormatted':
        filters[LoanFilterParamNames.date] = moment(valueObj.date).format('YYYY-MM-DD')
        filters[LoanFilterParamNames.dateCmp] = getDateComparatorStr(valueObj.comparator)
        break

      default: break
    }
  }

  return filters
},

// Extract sorting params from the sortName returned by BootstrapTable
getLoanSortKeyFromBootstrapTable = sortName => {

  let sortKey = null

  switch (sortName) {

    case 'clientName':
      sortKey = LoanFilterParamNames.client
      break

    case 'affiliated':
      sortKey = LoanFilterParamNames.affiliation
      break

    case 'bundleName':
      sortKey = LoanFilterParamNames.bundle
      break

    case 'collateralName':
      sortKey = LoanFilterParamNames.collateral
      break

    case 'imeisList':
      sortKey = LoanFilterParamNames.imei
      break

    case 'createdByName':
      sortKey = LoanFilterParamNames.createdBy
      break

    case 'assignToName':
      sortKey = LoanFilterParamNames.assignTo
      break

    case 'status':
      sortKey = LoanFilterParamNames.status
      break

    case 'disburseDateFormatted':
      sortKey = LoanFilterParamNames.date
      break

    default: break
  }

  return sortKey
},

// Extract sorting params from the sortName returned by BootstrapTable
getJournalSortKeyFromBootstrapTable = sortName => {

  let sortKey = null

  switch (sortName) {

    case 'id':
    case 'createdOnFormatted':
      sortKey = 'id'
      break

    case 'transactionId':
      sortKey = 'transactionId'
      break

    case 'transactionDateFormatted':
      sortKey = 'transactionDate'
      break

    case 'glAccountName':
      sortKey = 'glAccountName'
      break

    case 'reversed':
      sortKey = 'reversed'
      break

    default: break
  }

  return sortKey
},

// Extract sorting params from the sortName returned by BootstrapTable
getCustomerSortKeyFromBootstrapTable = sortName => {

  let sortKey = null

  switch (sortName) {

    case 'firstName':
      sortKey = 'firstName'
      break

    case 'lastName':
      sortKey = 'lastName'
      break

    case 'phoneNumber':
      sortKey = 'phoneNumber'
      break

    case 'affiliated':
      sortKey = 'affiliation'
      break

    case 'createdOnFormatted':
      sortKey = 'date'
      break

    default: break
  }

  return sortKey
},

buildLoanQueryString = ({ query: { page, size, group, client, affiliation,
  bundle, collateral, imei, createdBy, assignTo, status, date, dateCmp, sortBy, sortDir }}) => {

  const filteringOptions = {},
  groupName = getGroupFromQueryString(group)

  if (groupName !== GROUP_LIST.ALL) {
    filteringOptions.group = groupName
  }

  if (page) {
    filteringOptions.page = page - 1
  }

  if (size) {
    filteringOptions.size = size
  }

  if (client) {
    filteringOptions.client = client
  }

  if (affiliation) {
    filteringOptions.affiliation = affiliation
  }

  if (bundle) {
    filteringOptions.bundle = bundle
  }

  if (size) {
    filteringOptions.size = size
  }

  if (collateral) {
    filteringOptions.collateral = collateral
  }

  if (imei) {
    filteringOptions.imei = imei
  }

  if (createdBy) {
    filteringOptions.createdBy = createdBy
  }

  if (assignTo) {
    filteringOptions.assignTo = assignTo
  }

  if (status) {
    filteringOptions.status = status
  }

  if (date) {
    filteringOptions.date = date
  }

  if (dateCmp) {
    filteringOptions.dateCmp = dateCmp
  }

  if (sortBy) {
    filteringOptions.sortBy = sortBy
  }

  if (sortDir) {
    filteringOptions.sortDir = sortDir
  }

  return filteringOptions
},

buildJournalEntryQueryString = ({ query: { page, size, sortBy: orderBy,
  sortDir: sortOrder, fromDate, toDate, glAccountId, transactionId }}) => {

  const filteringOptions = {}

  const _size = size ? size : DEFAULT_NAV_SIZE
  filteringOptions.limit = _size

  const _page = page ? page : DEFAULT_NAV_PAGE
  filteringOptions.offset = (_page - 1) * _size

  // NB: Mifos orderBy = transactionDate, glAccountName, etc.
  const _orderBy = orderBy ? orderBy : 'id'
  filteringOptions.orderBy = _orderBy

  // NB: Mifos sortOrder = ASC, DESC 
  const _sortOrder = sortOrder ? sortOrder : 'DESC'
  filteringOptions.sortOrder = _sortOrder 

  if (fromDate) {
    filteringOptions.fromDate = fromDate
  }

  if (toDate) {
    filteringOptions.toDate = toDate
  }

  if (glAccountId) {
    filteringOptions.glAccountId = glAccountId
  }

  if (transactionId) {
    filteringOptions.transactionId = transactionId
  }

  return filteringOptions
},

buildCustomerQueryString = ({ query: { page, size, searchTerm, businessId, fromDate,
  toDate, sortBy, sortDir }}) => {

  const filteringOptions = {}

  if (page) {
    filteringOptions.page = page - 1
  }

  if (size) {
    filteringOptions.size = size
  }

  if (searchTerm) {
    filteringOptions.searchTerm = searchTerm
  }

  if (businessId) {
    filteringOptions.businessId = businessId
  }

  if (fromDate) {
    filteringOptions.fromDate = fromDate
  }

  if (toDate) {
    filteringOptions.toDate = toDate
  }

  if (sortBy) {
    filteringOptions.sortBy = sortBy
  }

  if (sortDir) {
    filteringOptions.sortDir = sortDir
  }

  return filteringOptions
},

/**
 * When BootstrapTable filter changed, update the query string
 */
handleFilterChange = (filters, filterParamNames, context, location) => {

  const { query } = location,
  newQuery = { ...query }

  for (const key in filterParamNames) {

    const currentParam = newQuery[key] || '',
    currentValue = filters[key] || ''

    if (currentParam.toLowerCase() !== currentValue.toString().toLowerCase()) {

      if (currentValue) {
        newQuery[key] = currentValue
      }
      else {
        delete newQuery[key]
      }
    }
  }

  // Route to the "first" page even when there's no query param update
  // This is important because when "onFilterChange" is called,
  // BootstrapTable automatically resets the currentPage to 1
  delete newQuery['page']
  context.router.push({
    pathname: location.pathname,
    query: newQuery,
  })
},

handleSortChange = (sortKey, sortOrder, context, location) =>{

  if (!sortKey) return

  const { query } = location,
  newQuery = { ...query }
  newQuery['sortBy'] = sortKey
  newQuery['sortDir'] = sortOrder.toUpperCase()

  context.router.push({
    pathname: location.pathname,
    query: newQuery,
  })
},

handleResetSorting = (context, location) => {

  const { query } = location,
  { sortBy, sortDir } = query

  if (sortBy || sortDir) {

    const newQuery = { ...query }
    delete newQuery['sortBy']
    delete newQuery['sortDir']

    context.router.push({
      pathname: location.pathname,
      query: newQuery,
    })
  }
}

export {
  LoanFilterParamNames,
  checkAndRoute,
  getLoanFiltersFromBootstrapTable,
  getLoanSortKeyFromBootstrapTable,
  getJournalSortKeyFromBootstrapTable,
  getCustomerSortKeyFromBootstrapTable,
  buildLoanQueryString,
  buildJournalEntryQueryString,
  buildCustomerQueryString,
  handleFilterChange,
  handleSortChange,
  handleResetSorting,
}


// WEBPACK FOOTER //
// ./src/lib/queryStringBuilder.js